import { forwardRef, useCallback, useMemo } from 'react'
import { Keyboard, Platform, useWindowDimensions } from 'react-native'
import { ReduceMotion } from 'react-native-reanimated'
import { BottomSheetBackdrop, BottomSheetModal } from '@gorhom/bottom-sheet'

import { DS } from '@/design-system'
import { styles } from '@/components/CheatSheet/CheatSheet.styles'
import { isWeb, useMaxWidth } from '../WebWrapper'
import { isNumber } from 'lodash'
import { SupportForm, type SupportFormProps } from './SupportForm'
import { supportFormModalRef, useSupportFormModal } from './useSupportFormModal'

// Note the default for `shouldRoute`
// This sheet should NOT use routing (e.g. web like 'back' action)
export const SupportFormSheet = forwardRef<BottomSheetModal, Omit<SupportFormProps, 'onClose'>>(
  ({ placeHolder, shouldRoute = false }, ref) => {
    const maxWidth = useMaxWidth()

    const windowDimensions = useWindowDimensions()
    const snapPoints = useMemo(() => (isWeb ? ['33%'] : ['45%']), [])
    const { close } = useSupportFormModal()

    const contentMargin = useMemo(() => {
      if (isWeb) {
        return undefined
      }
      if (!isNumber(maxWidth)) {
        return undefined
      }
      const widthDiff = Math.floor(windowDimensions.width - maxWidth)
      return widthDiff > 0 ? widthDiff * 0.5 : undefined
    }, [maxWidth, windowDimensions])

    const renderBackdrop = useCallback(
      (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
        <BottomSheetBackdrop
          {...props}
          pressBehavior={'close'}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          onPress={() => {
            requestAnimationFrame(() => {
              Keyboard.dismiss()
            })
          }}
        />
      ),
      []
    )

    return (
      <BottomSheetModal
        ref={ref}
        name={'supportFormModal'}
        index={0}
        snapPoints={snapPoints}
        enableDynamicSizing={true}
        maxDynamicContentSize={700}
        enableContentPanningGesture
        enableHandlePanningGesture
        enablePanDownToClose
        keyboardBehavior={Platform.OS === 'android' ? 'extend' : 'interactive'}
        keyboardBlurBehavior="none"
        android_keyboardInputMode="adjustResize"
        detached={false}
        handleStyle={styles.bottomSheetHandleStyle}
        handleIndicatorStyle={styles.bottomSheetIndicatorStyle}
        backgroundStyle={{ backgroundColor: DS.palette['Brand/Cream'] }}
        backdropComponent={renderBackdrop}
        overrideReduceMotion={ReduceMotion.System}
        style={{
          marginBottom: 64,
          marginHorizontal: isWeb ? 'auto' : undefined,
          maxWidth: maxWidth,
          width: '100%'
        }}
        containerStyle={{
          // NOTE: This is to force a margin for iPads
          marginLeft: contentMargin
        }}
      >
        <SupportForm placeHolder={placeHolder} shouldRoute={shouldRoute} onClose={close} />
      </BottomSheetModal>
    )
  }
)
